// Modal para que admin asigne profesor + licencia a un alumno.

import { useEffect, useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { Button } from '@/components/atoms/Button';
import { Icon } from '@/components/atoms/Icon';
import { ApiError } from '@/services/api';
import { listAdminUsers, updateAssignment, type AdminUser } from '@/services/admin';
import { listLicenses, type CatalogLicense } from '@/services/catalogs';
import styles from './AssignmentDialog.module.scss';

interface AssignmentDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  user: AdminUser | null;
  onSaved: () => void;
}

export function AssignmentDialog({ open, onOpenChange, user, onSaved }: AssignmentDialogProps) {
  const [teachers, setTeachers] = useState<AdminUser[]>([]);
  const [licenses, setLicenses] = useState<CatalogLicense[]>([]);
  const [teacherId, setTeacherId] = useState<string>('');
  const [licenseId, setLicenseId] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !user) return;
    setLoading(true);
    setError(null);
    setTeacherId(user.assigned_teacher_id ? String(user.assigned_teacher_id) : '');
    setLicenseId(user.license_id ? String(user.license_id) : '');
    Promise.all([
      listAdminUsers({ role: 'teacher', limit: 200 }),
      listLicenses(),
    ])
      .then(([t, l]) => {
        setTeachers(t.items);
        setLicenses(l);
      })
      .catch((e) => setError(e instanceof Error ? e.message : 'Error al cargar'))
      .finally(() => setLoading(false));
  }, [open, user]);

  async function save() {
    if (!user) return;
    setSubmitting(true);
    setError(null);
    try {
      await updateAssignment(user.id, {
        assigned_teacher_id: teacherId ? Number(teacherId) : null,
        license_id: licenseId ? Number(licenseId) : null,
      });
      onSaved();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : e instanceof Error ? e.message : 'Error al guardar');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className={styles.overlay} />
        <Dialog.Content className={styles.content} aria-describedby={undefined}>
          <header className={styles.header}>
            <Dialog.Title className={styles.title}>Asignar profesor y licencia</Dialog.Title>
            <Dialog.Close asChild>
              <button className={styles.closeBtn} aria-label="Cerrar">
                <Icon name="x" size={20} />
              </button>
            </Dialog.Close>
          </header>

          {!user ? null : (
            <div className={styles.body}>
              <div className={styles.userInfo}>
                <strong>{user.display_name}</strong>
                <span>{user.email}</span>
                <span className={styles.muted}>{user.roles}</span>
              </div>

              {loading ? (
                <p className={styles.muted}>Cargando…</p>
              ) : (
                <>
                  <label className={styles.field}>
                    <span>Profesor asignado</span>
                    <select value={teacherId} onChange={(e) => setTeacherId(e.target.value)}>
                      <option value="">— Sin asignar —</option>
                      {teachers.map((t) => (
                        <option key={t.id} value={t.id}>{t.display_name} ({t.email})</option>
                      ))}
                    </select>
                  </label>

                  <label className={styles.field}>
                    <span>Licencia</span>
                    <select value={licenseId} onChange={(e) => setLicenseId(e.target.value)}>
                      <option value="">— Sin licencia —</option>
                      {licenses.map((l) => (
                        <option key={l.id} value={l.id}>
                          {l.label} · {l.monthly_sessions_one || l.monthly_sessions_gr} clases/mes
                        </option>
                      ))}
                    </select>
                  </label>
                </>
              )}

              {error && <p className={styles.error}>{error}</p>}

              <footer className={styles.footer}>
                <Dialog.Close asChild>
                  <Button variant="secondary">Cancelar</Button>
                </Dialog.Close>
                <Button variant="primary" disabled={loading || submitting} onClick={() => void save()}>
                  {submitting ? 'Guardando…' : 'Guardar cambios'}
                </Button>
              </footer>
            </div>
          )}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
